import Layout from "../layout";
import { useAuth } from "react-oidc-context";
import { Card } from "react-bootstrap";
import MessageStats from "../../components/messagestats/messagestats";
import TimeFilter from "../../components/TimeFilter/TimeFilter";
import { useState } from "react";
import React from "react";
import { timeUnit } from "../../app/enums";
import { TopMessagesType } from "../../models/TopMessagesType";
import { ServiceManager } from "../../services/ServiceManager";
import { useQuery } from "@tanstack/react-query";
import HighestMessages from "./HighestMessages";
import PendingMessages from "./PendingMessages";
import EstablishmentsMessages from "./EstablishmentsMessages";
import LogLevelEstablishments from "./LogLevelEstablishments";
import HUBTooltip from "../../components/tooltip";
import { faInfoCircle } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { jwtDecode } from "jwt-decode";

export default function Dashboard() {
  const auth = useAuth();

  const [selectedTimer, setSelectedTimer] = useState(30);
  const [selectedTimeUnit, setselectedTimeUnit] = useState(timeUnit.minute);
  const [finishDate, setFinishDate] = useState(new Date());
  var defaultStartDate = new Date();
  defaultStartDate.setMinutes(defaultStartDate.getMinutes() - 30);
  const [startDate, setStartDate] = useState(defaultStartDate);

  var jwt: any;

  if (auth.isAuthenticated) {
    jwt = jwtDecode(auth.user?.access_token ?? '', { header: false });
  }

  // Fetch time-sensitive data for MessageStats (refreshes with date range changes)
  const { isLoading: isLoadingMessageStats } = useQuery({
    queryKey: [`message-stats`, startDate, finishDate],
    queryFn: () => ServiceManager.MessageService.GetTopMessages(),
    refetchOnWindowFocus: false
  });

  // Check isLoadingMessageStats to prevent ESLint @typescript-eslint/no-unused-vars error.
  // This ensures the query is executed for its side effects (caching/refetching behavior),
  // while the actual data is retrieved from the static query below. In the future, this
  // loading state can be used to show a loading indicator or spinner for time-sensitive data updates.
  if (isLoadingMessageStats) {
    // TODO: Consider showing loading state UI or placeholder while time-sensitive data is being fetched
  }

  // Fetch static data (doesn't refresh with date range changes)
  const { isLoading: isLoadingStatic, data } = useQuery({
    queryKey: [`top-messages-static`],
    queryFn: () => ServiceManager.MessageService.GetTopMessages(),
    refetchOnWindowFocus: false
  });

  // Extract data for each message type (all from static query)
  const highestMessagesData = data?.data?.data?.find((x: any) => x.type === TopMessagesType.HighestMessages)?.messages || [];
  const pendingMessagesData = data?.data?.data?.find((x: any) => x.type === TopMessagesType.PendingMessages)?.messages || [];
  const establishmentsMessagesData = data?.data?.data?.find((x: any) => x.type === TopMessagesType.Establishments)?.messages || [];
  const logLevelEstablishmentsData = data?.data?.data?.find((x: any) => x.type === TopMessagesType.LogLevelEstablishments)?.messages || [];

  function setTimes(time: any, timeUnit: timeUnit, callBackStartDate: Date, callBackfinishDate: Date, isRefresh?: boolean) {
    if (time === '') {
      return;
    }

    setSelectedTimer(time);
    setselectedTimeUnit(timeUnit);
    setFinishDate(callBackfinishDate);
    setStartDate(callBackStartDate);
  }

  return (
    <React.Fragment>
      {auth.isAuthenticated && <Layout title={auth.isAuthenticated && "Welcome " + jwt.name} enableScroll={true}>
        <div className="container-fluid">
          <div className="row">
            <TimeFilter selectedTimer={selectedTimer} selectedTimeUnit={selectedTimeUnit} setTimes={setTimes} isRefresh={true}></TimeFilter>
          </div>
          <div className="row">
            <div className="col-xl-4 col-6">
              <MessageStats selectedTimer={selectedTimer} startDate={startDate} finishDate={finishDate} navigateTo={`/message?startDate=${startDate.toISOString()}&finishDate=${finishDate.toISOString()}&timer=${selectedTimer}&unit=${selectedTimeUnit}&fromDashboard=${true}&logLevel=3,4,5`}></MessageStats>
            </div>
          </div>
          <div className="row mt-4">
            <div className="col-6">
              <Card className="stats-table-widget">
                <Card.Header>
                  <Card.Title className="pt-3 ps-2 pb-3 d-flex align-items-center gap-2">Highest Messages
                    <span style={{ display: 'flex', alignItems: 'center', marginTop: '4px' }}>
                      <HUBTooltip message="Top 10 most frequently occurring log messages. Shows which errors are happening most often." placement="right">
                        <FontAwesomeIcon
                          icon={faInfoCircle}
                          size="sm"
                        />                    
                      </HUBTooltip>
                    </span>
                  </Card.Title>
                </Card.Header>
                <Card.Body className="p-0">
                  <HighestMessages data={highestMessagesData} isLoading={isLoadingStatic} />
                </Card.Body>
              </Card>
            </div>
            <div className="col-6">
              <Card className="stats-table-widget">
                <Card.Header>
                  <Card.Title className="pt-3 ps-2 pb-3 d-flex align-items-center gap-2">Pending Messages
                    <span style={{ display: 'flex', alignItems: 'center', marginTop: '4px' }}>
                      <HUBTooltip message="Top 10 log messages with the longest duration (days between creation and last seen). Shows which errors have been persisting the longest." placement="right">
                        <FontAwesomeIcon
                          icon={faInfoCircle}
                          size="sm"
                        />
                      </HUBTooltip>
                    </span>
                  </Card.Title>
                </Card.Header>
                <Card.Body className="p-0">
                  <PendingMessages data={pendingMessagesData} isLoading={isLoadingStatic}/>
                </Card.Body>
              </Card>
            </div>
            <div className="col-6 mt-5">
              <Card className="stats-table-widget h-100">
                <Card.Header>
                  <Card.Title className="pt-3 ps-2 pb-3 d-flex align-items-center gap-2">Establishments Messages
                    <span style={{ display: 'flex', alignItems: 'center', marginTop: '4px' }}>
                      <HUBTooltip message="Top 10 establishments (by total error instance count), but limited to the top 5 messages per establishment. This gives a hierarchical view: which establishments have the most errors, and what are their most critical/frequent issues." placement="right">
                        <FontAwesomeIcon
                          icon={faInfoCircle}
                          size="sm"
                        />
                      </HUBTooltip>
                    </span>
                  </Card.Title>
                </Card.Header>
                <Card.Body className="p-0">
                  <EstablishmentsMessages data={establishmentsMessagesData} isLoading={isLoadingStatic}/>
                </Card.Body>
              </Card>
            </div>
            <div className="col-6 mt-5">
              <Card className="stats-table-widget h-100">
                <Card.Header>
                  <Card.Title className="pt-3 ps-2 pb-3 d-flex align-items-center gap-2">Establishments by Log Level
                    <span style={{ display: 'flex', alignItems: 'center', marginTop: '4px' }}>
                      <HUBTooltip message="Top 10 establishments with the highest volume of trace, debug, and information level logs. Shows which establishments are generating the most diagnostic/non-error logs." placement="right">
                        <FontAwesomeIcon
                          icon={faInfoCircle}
                          size="sm"
                        />
                      </HUBTooltip>
                    </span>
                  </Card.Title>
                </Card.Header>
                <Card.Body className="p-0">
                  <LogLevelEstablishments data={logLevelEstablishmentsData} isLoading={isLoadingStatic}/>
                </Card.Body>
              </Card>
            </div>        
          </div>
        </div>
      </Layout>}
    </React.Fragment>
  );
}